// frontend/src/queries/keys.js
//
// Centralized query key factory. Every `useQuery({ queryKey: ... })` call
// in the app should pull its key from this file so:
//   - Renames are one-line (every consumer reads through the factory)
//   - Invalidation has one home: `queries/dataChanged.js` lists what each
//     `.all()` prefix reads, and a save names kinds of data, never keys
//   - The hierarchy is documented — anyone reading this file sees the
//     full universe of queries the app fires, plus their scope (tenant /
//     store / item-level)
//
// Conventions:
//   - Each domain exports `.all()` (the prefix dataChanged invalidates)
//     and `.detail(id)` / `.byX(x)` for parameterized queries
//   - Store-scoped queries put `{ storeId }` as a segment so the auth
//     boundary cache reset (clearStoreScopedQueries) can find them
//   - Store-invariant queries (countries, currencies, tabular entities)
//     have no scoping segment — cached across store switches
//   - Filter / pagination params live as the LAST object in the key so
//     `invalidateQueries({ queryKey: keys.foo.all() })` matches all
//     filtered variants (TanStack does prefix matching by default)
//
// Don't add a key nobody reads.

// Reference data (no scope — static across the app session).
export const reference = {
  countries:           () => ['reference', 'countries'],
  currencies:          () => ['reference', 'currencies'],
  // What each entity's import can do (mode + blank-policy columns). Static
  // per deployment — identical for every store and organization — so it sits
  // here unscoped and is fetched once per session.
  tabularEntities:     () => ['reference', 'tabularEntities'],
};

// The store's location types (`/api/location-types/`): the server lists the
// selected store's rows (api/views/viewsets.py LocationTypeViewSet through
// StoreAwareViewSet.get_queryset), so the key is store-scoped like any other
// store row and a store switch drops it.
export const locationTypes = {
  all: (storeId) => ['locationTypes', { storeId }],
};

// The build the static host serves now (/version.json) — the same for every
// store and user.
export const appVersion = {
  deployed: () => ['appVersion', 'deployed'],
};

// Per signed-in user: `/auth/users/` lists the peers of every organization the
// caller belongs to, whatever store is selected. Logout clears the cache.
export const users = {
  all: () => ['users'],
};

// The signed-in person's own profile (`/auth/user-profile/me/`). Per person,
// not per store; logout drops it with the rest of the cache.
export const userProfile = {
  me: () => ['userProfile', 'me'],
};

// The selected store's sync progress (`/api/store/sync-status/`), polled at
// the interval `utils/syncGate.syncPollInterval` names. One key backs every
// consumer so the entry screen, the sidebar's `SyncProgressPill` and the Home
// forecast tiles share one poll loop.
export const syncStatus = {
  all: (storeId) => ['syncStatus', { storeId }],
};

// The selected store's band, prices and plan ladder (`/api/billing/pricing/`),
// read by the Plans page. The paywall reads the same endpoint outside the
// tabbed app, before a QueryClient-backed page could mount.
export const billingPricing = {
  all: (storeId) => ['billingPricing', { storeId }],
};

// Store-scoped data — `{ storeId }` segment makes these eligible for
// clearStoreScopedQueries() on store switch.
export const suppliers = {
  all:  (storeId) => ['suppliers', { storeId }],
  detail: (storeId, id) => ['suppliers', { storeId }, 'detail', id],
};

export const calendars = {
  all: (storeId) => ['calendars', { storeId }],
};

export const locations = {
  all: (storeId) => ['locations', { storeId }],
};

// Product category tree (sourced from Shopify's standard taxonomy, synced
// weekly into core.ProductCategory). Two cache regions, one per picker
// operation:
//   - search()        — assignment (ItemSetup, DataHealth, Add Item).
//   - inStoreSearch() — targeting. Only categories assigned to the store's
//                       items — promotions, report filters.
// all() covers both branches; inStoreAll() the targeting branch alone, which
// an item save moves.
//
// EVERY branch is keyed per storeId. /api/product-categories/ is mode-aware
// (api/views/viewsets.py: ProductCategoryViewSet.get_queryset): a taxonomy
// store gets the shared Shopify tree, a PRODUCT_TYPE store gets its OWN
// derived product-type nodes. Same URL, store-dependent rows.
export const productCategories = {
  all: (storeId) => ['productCategories', { storeId }],
  inStoreAll: (storeId) => ['productCategories', { storeId }, 'in-store'],
  search: (storeId, term) => ['productCategories', { storeId }, 'search', term || ''],
  // One page of a search — backs the grid Rich Select's infinite paging.
  // Nested under the 'search' prefix so invalidating ['productCategories']
  // still clears it.
  searchPage: (storeId, term, page) => [
    'productCategories', { storeId }, 'search', term || '', 'page', page,
  ],
  inStoreSearch: (storeId, term) => [
    'productCategories', { storeId }, 'in-store', term || '',
  ],
};

// One item's supplier relations — GET /api/supplier-catalog-items/?item=<id>,
// read by the per-item Suppliers dialog (components/supplier/
// ItemSuppliersDialog.jsx).
export const supplierCatalogItems = {
  all: (storeId) => ['supplierCatalogItems', { storeId }],
  forItem: (storeId, itemId) => ['supplierCatalogItems', { storeId }, 'item', String(itemId)],
};

export const dataBounds = {
  all: (storeId) => ['dataBounds', { storeId }],
};

// KPI tiles — store-scoped so a store switch invalidates them. Each KPI
// is a single fetch with no params, so the key is just the namespace.
export const kpi = {
  inventory: (storeId) => ['kpi', 'inventory', { storeId }],
  forecast: (storeId) => ['kpi', 'forecast', { storeId }],
  sales: (storeId) => ['kpi', 'sales', { storeId }],
  shortage: (storeId) => ['kpi', 'shortage', { storeId }],
  excess: (storeId) => ['kpi', 'excess', { storeId }],
};

// Bell-icon notification badge. One key for one endpoint
// (/api/notifications/badge/) that returns all three signals (data
// health issues, PO recommendations due today, outstanding POs with
// deliveries due today / overdue). Store-scoped.
export const notifications = {
  badge: (storeId) => ['notifications', 'badge', { storeId }],
};

// Home-page summary tables — list of items, store-scoped. Same caching
// profile as KPIs (30s staleTime) since they're "quick glance" data.
export const homeTables = {
  shortageItems: (storeId) => ['homeTables', 'shortageItems', { storeId }],
  outstandingPOs: (storeId) => ['homeTables', 'outstandingPOs', { storeId }],
};

// Reorder levels (`/api/reorder-levels/`). `status` is the one cached read:
// whether the store has forecast demand and whether to show the welcome
// popup. The grid's rows are server-side (useSsrmGrid), not a query.
export const reorderLevels = {
  all:    (storeId) => ['reorderLevels', { storeId }],
  status: (storeId) => ['reorderLevels', { storeId }, 'status'],
};

// Chart timeline data — store-scoped + heavily parameterised (metric,
// granularity, drill-down filters, etc.). The full params dict goes into
// the key so different filter combinations cache independently — the
// same chart with metric=qty vs metric=value produces two distinct
// queries that can both stay warm.
export const timelines = {
  inventory: (storeId, params) => ['timeline', 'inventory', { storeId }, params],
  // The `*All` keys are the param-less prefixes for invalidation: each
  // matches every filter/metric variant of its timeline in one call. A
  // prefix must END at `{ storeId }` — a trailing `undefined` element is
  // compared like any other and matches no cached variant.
  inventoryAll: (storeId) => ['timeline', 'inventory', { storeId }],
  demand: (storeId, params) => ['timeline', 'demand', { storeId }, params],
  demandAll: (storeId) => ['timeline', 'demand', { storeId }],
  salesProgress: (storeId, params) => ['timeline', 'salesProgress', { storeId }, params],
  salesProgressAll: (storeId) => ['timeline', 'salesProgress', { storeId }],
};

// Promotions — the store's whole list (the page filters it by status), and
// one promotion's items, under the same root so a promotion change reloads
// both.
export const promotions = {
  all:     (storeId)              => ['promotions', { storeId }],
  list:    (storeId)              => ['promotions', { storeId }, 'list'],
  members: (storeId, promotionId) => ['promotions', { storeId }, 'members', promotionId],
};

// Purchase order header detail — cached per (storeId, poId).
export const purchaseOrders = {
  all:    (storeId)     => ['purchaseOrders', { storeId }],
  detail: (storeId, id) => ['purchaseOrders', { storeId }, 'detail', id],
  issueDate: (storeId, id) => ['purchaseOrders', { storeId }, 'issueDate', id],
};

// Purchase order receipt history — cached per (storeId, poId). Detail
// rows on the PO grid each fetch their own history; sharing the cache
// across rows means expanding two POs and collapsing+re-expanding the
// first doesn't re-hit the backend.
export const poReceiptHistory = {
  all: (storeId) => ['poReceiptHistory', { storeId }],
  byPo: (storeId, poId) => ['poReceiptHistory', { storeId }, poId],
};

// Recommendation engine planning summary (PO Recommendations top banner).
export const poPlanningSummary = {
  all: (storeId) => ['poPlanningSummary', { storeId }],
};

// Customer order lines — cached per (storeId, orderId). Detail rows on the
// Customer Orders grid each fetch their own lines (CustomerOrderDetail);
// collapsing + re-expanding an order reuses the cache.
export const customerOrderLines = {
  all: (storeId) => ['customerOrderLines', { storeId }],
  byOrder: (storeId, orderId) => ['customerOrderLines', { storeId }, orderId],
};

// PO recommendation lines — per (storeId, recId). Detail rows on the
// Purchase Recommendations grid (RecoDetail). Every calculation run DELETES
// all pending recommendations and recreates them with NEW ids
// (purchase_orders/services.py _replace_pending_recommendations) — a recId is
// valid only within one run — so consumers use staleTime 0: the key dedupes
// concurrent fetches but never serves a pre-recalc snapshot, and a 404 is a
// staleness signal (run superseded), not a fetch error.
export const poRecommendationLines = {
  all: (storeId) => ['poRecommendationLines', { storeId }],
  byRec: (storeId, recId) => ['poRecommendationLines', { storeId }, recId],
};

// Calendar overrides — recurring annual holidays + one-off date
// exceptions. Each list is per-calendar (small payloads: ~10 holidays,
// ~30 exceptions). Storing all of a calendar's overrides in a single
// cache entry lets the month-grid render any month entirely
// client-side, no per-month round-trip.
export const calendarHolidays = {
  all: (storeId)                 => ['calendarHolidays', { storeId }],
  byCalendar: (storeId, calId)   => ['calendarHolidays', { storeId }, calId],
};

export const calendarExceptions = {
  all: (storeId)                 => ['calendarExceptions', { storeId }],
  byCalendar: (storeId, calId)   => ['calendarExceptions', { storeId }, calId],
};

// Data Health summary — the full version (the bell-icon badge uses its own
// endpoint, see notifications.badge).
export const dataHealthSummary = {
  all: (storeId) => ['dataHealthSummary', { storeId }],
};

// Suspected cross-origin duplicate item pairs awaiting merchant merge
// (the standing identity surface). Store-scoped.
export const identityDuplicates = {
  all: (storeId) => ['identityDuplicates', { storeId }],
};

// Forecast generator statistics (forecast count + last run + which
// items are eligible). Mount-on-render data on the Forecast Generator
// page.
export const forecastStatistics = {
  all: (storeId) => ['forecastStatistics', { storeId }],
};

// The Store settings page payload — organization, store, locale, units,
// planning, PO-PDF columns and the category-source block in one GET. Every
// section save PATCHes the same endpoint and writes the response straight
// back into this key, so the page shows what it just saved at once.
export const storeSettings = {
  all: (storeId) => ['storeSettings', { storeId }],
  // The planning slice on its own, for screens that need the store's
  // defaults without the settings page's `{settings, forms}` shape
  // (hooks/useStorePlanning).
  planning: (storeId) => ['storeSettings', { storeId }, 'planning'],
};

// Which columns this store's spreadsheet exports carry. A separate key from
// storeSettings because it is a separate endpoint (/api/export-columns/): the
// catalog half is derived from api.tabular_registry, and the dialog that edits
// it opens on demand rather than loading with the settings page.
export const exportColumns = {
  all: (storeId) => ['exportColumns', { storeId }],
};

// Cost import — the store's latest COST_IMPORT pipeline run (status,
// progress, and the counters it finished with). Polled while the run is in
// flight; see hooks/useCostImport.js.
export const costImport = {
  status: (storeId) => ['costImport', { storeId }, 'status'],
};

// The Supplier setup page's "Create from Shopify vendors" run (backend:
// api/services/catalog_derivations.py vendor_suppliers_state).
export const vendorSuppliers = {
  status: (storeId) => ['vendorSuppliers', { storeId }, 'status'],
};

// Generated exports — the store's ExportJob rows. An export too large to
// answer inside a request is produced on the pipeline rail and downloaded
// afterwards, so this list is the ONLY way back to a file whose originating
// tab has gone away (backend: core/models.py ExportJob).
export const exportJobs = {
  all: (storeId) => ['exportJobs', { storeId }],
};

// Connectors — the per-store list of ConnectorInstance rows for the
// Connectors settings page, plus the Exact supplier-selection rows the
// Resolve wizard reads while a connector is parked at RESOLVE.
export const connectors = {
  all: (storeId) => ['connectors', { storeId }],
  // Who owns locations / suppliers (/api/connectors/sor/). Under the `all`
  // prefix on purpose: a connector change re-reads this answer too.
  sor: (storeId) => ['connectors', { storeId }, 'sor'],
  // Read-only activity detail for one connector (the "Details" drawer):
  // health, last sync, counts, pending reviews, recent webhook deliveries.
  activity: (storeId, connectorId) =>
    ['connectors', { storeId }, 'activity', connectorId],
  supplierSelection: (storeId, connectorId) =>
    ['connectors', { storeId }, 'exact-supplier-selection', connectorId],
  itemSelection: (storeId, connectorId) =>
    ['connectors', { storeId }, 'exact-item-selection', connectorId],
  locationSelection: (storeId, connectorId) =>
    ['connectors', { storeId }, 'exact-location-selection', connectorId],
  poSelection: (storeId, connectorId) =>
    ['connectors', { storeId }, 'exact-po-selection', connectorId],
  disconnectPreview: (storeId, connectorId) =>
    ['connectors', { storeId }, 'exact-disconnect-preview', connectorId],
};
